import { Box, IconButton, Image, ListItem, OrderedList, Text, UnorderedList, } from "@chakra-ui/react";
import React from "react";
import { MdDelete } from "react-icons/md";
import { Editor, Element as SlateElement, Transforms, Text as SlateText, } from "slate";
import { ReactEditor, useFocused, useSelected, useSlate } from "slate-react";
import { supportedDeserializerTypes, } from "../types";
import { deserialize, getPreferedFormat, isUrl } from "./functions";
export const initialSlateValue = [
    {
        type: "paragraph",
        children: [
            {
                text: "paragraph",
                fontSize: 16,
                font: "Poppins",
                color: "#FFFFFF",
            },
        ],
    },
];
const LIST_TYPES = ["check-list-item", "bulleted-list"];
const TEXT_ALIGN_TYPES = ["left", "center", "right", "justify"];
const toggleBlock = (editor, format) => {
    const isActive = isBlockActive(editor, format, TEXT_ALIGN_TYPES.some((align) => align === format) ? "align" : "type");
    const isList = LIST_TYPES.includes(format);
    Transforms.unwrapNodes(editor, {
        match: (n) => !Editor.isEditor(n) &&
            SlateElement.isElement(n) &&
            LIST_TYPES.includes(n.type) &&
            !TEXT_ALIGN_TYPES.some((align) => align === format),
        split: true,
    });
    let newProperties;
    if (TEXT_ALIGN_TYPES.some((align) => align === format)) {
        newProperties = {
            align: isActive ? undefined : format,
        };
    }
    else {
        newProperties = {
            type: isActive
                ? "paragraph"
                : isList
                    ? "list-item"
                    : format,
        };
    }
    Transforms.setNodes(editor, newProperties);
    if (!isActive && isList) {
        const block = {
            type: format,
            children: [],
        };
        Transforms.wrapNodes(editor, block);
    }
};
export const toggleMark = (editor, format, value = true) => {
    const isActive = isMarkActive(editor, format);
    const selection = editor.selection;
    const anchor = selection === null || selection === void 0 ? void 0 : selection.anchor;
    const focus = selection === null || selection === void 0 ? void 0 : selection.focus;
    if ((anchor === null || anchor === void 0 ? void 0 : anchor.offset) === (focus === null || focus === void 0 ? void 0 : focus.offset)) {
        Transforms.setNodes(editor, {
            [format]: typeof value === "boolean" ? !isActive : value,
        }, {
            match: (node) => {
                return SlateText.isText(node);
            },
            // @ts-ignore
            at: {
                anchor: { path: [0] },
                focus: { path: [editor.children.length] },
            },
        });
    }
    if (isActive) {
        Editor.removeMark(editor, format);
    }
    else {
        Editor.addMark(editor, format, value);
    }
};
const isBlockActive = (editor, format, blockType = "type") => {
    const { selection } = editor;
    if (!selection)
        return false;
    const [match] = Array.from(Editor.nodes(editor, {
        at: Editor.unhangRange(editor, selection),
        match: (n) => !Editor.isEditor(n) &&
            SlateElement.isElement(n) &&
            n[blockType] === format,
    }));
    return !!match;
};
export const isMarkActive = (editor, format) => {
    const marks = Editor.marks(editor);
    return marks ? marks[format] === true : false;
};
export const Element = ({ attributes, children, element, scale = 1, editor, }) => {
    var _a;
    const selected = useSelected();
    const focused = useFocused();
    const style = {
        textAlign: element.align,
    };
    let path;
    if (editor) {
        path = ReactEditor.findPath(editor, element);
    }
    switch (element.type) {
        case "paragraph":
            return (React.createElement(Text, Object.assign({ style: style }, attributes, { height: "fit-content" }), children));
        case "bulleted-list":
            return (React.createElement(UnorderedList, Object.assign({ style: style }, attributes), children));
        case "list-item":
            return (React.createElement(ListItem, Object.assign({ style: style }, attributes), children));
        case "numbered-list":
            return (React.createElement(OrderedList, Object.assign({ style: style }, attributes), children));
        case "image":
            return (React.createElement(Box, Object.assign({}, attributes),
                children,
                React.createElement(Box, { position: "relative", contentEditable: false, style: style },
                    editor ? (React.createElement(IconButton, { width: `calc(32px * ${1 / scale})`, height: `calc(32px * ${1 / scale})`, minWidth: "unset", position: "absolute", top: "2", left: "2", "aria-label": "Supprimer l'image", bgColor: "#FFFFFF", icon: React.createElement(MdDelete, { style: { width: "66%", height: "66%" } }), onClick: () => Transforms.removeNodes(editor, { at: path }) })) : (React.createElement(React.Fragment, null)),
                    React.createElement(Image, { src: (_a = element.url) !== null && _a !== void 0 ? _a : element.base64data, alt: "", maxWidth: "80%", minWidth: "30%", display: "inline", boxShadow: selected && focused
                            ? `0 0 0 calc(${3 * (1 / scale)}px) #B4D5FF`
                            : "none" }))));
        default:
            return (React.createElement(Text, Object.assign({ style: style }, attributes), children));
    }
};
export const Leaf = ({ attributes, children, leaf, scale = 1, }) => {
    let style = {};
    if (leaf.bold) {
        style = Object.assign(Object.assign({}, style), { fontWeight: "bold" });
    }
    if (leaf.italic) {
        style = Object.assign(Object.assign({}, style), { fontStyle: "italic" });
    }
    if (leaf.underline) {
        style = Object.assign(Object.assign({}, style), { textDecoration: "underline" });
    }
    if (leaf.color) {
        style = Object.assign(Object.assign({}, style), { color: leaf.color });
    }
    if (leaf.backgroundColor) {
        style = Object.assign(Object.assign({}, style), { backgroundColor: leaf.backgroundColor });
    }
    if (leaf.font) {
        style = Object.assign(Object.assign({}, style), { fontFamily: leaf.font });
    }
    if (leaf.fontSize) {
        style = Object.assign(Object.assign({}, style), { fontSize: `${leaf.fontSize}px` });
    }
    return (React.createElement(Box, Object.assign({ as: "span" }, attributes, { sx: style, whiteSpace: "pre-wrap" }), children));
};
export const BlockButton = ({ format, icon, }) => {
    const editor = useSlate();
    return (React.createElement(IconButton, { size: "sm", "aria-label": "Aligner Droite", icon: icon, isActive: isBlockActive(editor, format, TEXT_ALIGN_TYPES.some((align) => align === format) ? "align" : "type"), onMouseDown: (e) => {
            e.preventDefault();
            toggleBlock(editor, format);
        } }));
};
export const MarkButton = ({ format, icon, }) => {
    const editor = useSlate();
    return (
    // <Center
    //   role="button"
    //   aria-label="Aligner Droite"
    //   onMouseDown={(event) => {
    //     event.preventDefault();
    //     toggleMark(editor, format);
    //   }}
    //   p={1}
    //   borderRadius="4px"
    //   transitionDuration="0.2s"
    //   sx={{
    //     ":hover": {
    //       backgroundColor: "gray.200",
    //     },
    //   }}
    // >
    //   <Icon size="20px" />
    // </Center>
    React.createElement(IconButton, { size: "sm", "aria-label": "Aligner Droite", icon: icon, isActive: isMarkActive(editor, format), onMouseDown: (event) => {
            event.preventDefault();
            toggleMark(editor, format);
        } })
    // <IconButton
    //   isActive={isMarkActive(editor, format)}
    // />
    );
};
export const withImages = (editor) => {
    const { isVoid, insertData, insertFragment } = editor;
    editor.isVoid = (element) => {
        return element.type === "image" ? true : isVoid(element);
    };
    editor.insertData = (data) => {
        const text = data.getData("text/plain");
        //   const { files } = data;
        //   if (files && files.length > 0) {
        //     for (const file of files) {
        //       const reader = new FileReader();
        //       const [mime] = file.type.split("/");
        //       if (mime === "image") {
        //         reader.addEventListener("load", () => {
        //           insertImage({ editor, base64data: reader.result as string });
        //         });
        //         reader.readAsDataURL(file);
        //       }
        //     }
        //   } else
        if (isImageUrl(text)) {
            return insertImage({ editor, url: text });
        }
        const supportedTypes = data.types.filter((type) => 
        // @ts-ignore
        supportedDeserializerTypes.includes(type));
        if (supportedTypes.length === 0) {
            return insertData(data);
        }
        let preferedFormat = getPreferedFormat(supportedTypes);
        const deserializedData = deserialize(data, preferedFormat);
        if (deserializedData === null) {
            return insertData(data);
        }
        insertFragment(deserializedData);
    };
    return editor;
};
export const DisplaySlate = ({ text, scale }) => {
    return (React.createElement(React.Fragment, null, text.map((item, index) => {
        if (item.type) {
            const typedItem = item;
            return (React.createElement(Element, { element: typedItem, attributes: {}, scale: scale, key: `slate-element-${typedItem.type}-${index}` },
                React.createElement(DisplaySlate, { text: typedItem.children, scale: scale })));
        }
        const typedItem = item;
        return (React.createElement(Leaf, { key: `slate-leaf-${index}`, leaf: typedItem, attributes: {}, scale: scale }, typedItem.text === "" ? " " : typedItem.text));
    })));
};
const insertImage = ({ editor, url, base64data }) => {
    const text = [{ text: "" }];
    const image = {
        type: "image",
        // base64data,
        url,
        children: text,
    };
    Transforms.insertNodes(editor, image);
};
const isImageUrl = (url) => {
    var _a;
    if (!url)
        return false;
    if (!isUrl(url))
        return false;
    const ext = (_a = new URL(url).pathname.split(".").pop()) !== null && _a !== void 0 ? _a : "";
    return imageExtensions.includes(ext);
};
const imageExtensions = [
    "ase",
    "art",
    "bmp",
    "blp",
    "cd5",
    "cit",
    "cpt",
    "cr2",
    "cut",
    "dds",
    "dib",
    "djvu",
    "egt",
    "exif",
    "gif",
    "gpl",
    "grf",
    "icns",
    "ico",
    "iff",
    "jng",
    "jpeg",
    "jpg",
    "jfif",
    "jp2",
    "jps",
    "lbm",
    "max",
    "miff",
    "mng",
    "msp",
    "nef",
    "nitf",
    "ota",
    "pbm",
    "pc1",
    "pc2",
    "pc3",
    "pcf",
    "pcx",
    "pdn",
    "pgm",
    "PI1",
    "PI2",
    "PI3",
    "pict",
    "pct",
    "pnm",
    "pns",
    "ppm",
    "psb",
    "psd",
    "pdd",
    "psp",
    "px",
    "pxm",
    "pxr",
    "qfx",
    "raw",
    "rle",
    "sct",
    "sgi",
    "rgb",
    "int",
    "bw",
    "tga",
    "tiff",
    "tif",
    "vtf",
    "xbm",
    "xcf",
    "xpm",
    "3dv",
    "amf",
    "ai",
    "awg",
    "cgm",
    "cdr",
    "cmx",
    "dxf",
    "e2d",
    "egt",
    "eps",
    "fs",
    "gbr",
    "odg",
    "svg",
    "stl",
    "vrml",
    "x3d",
    "sxd",
    "v2d",
    "vnd",
    "wmf",
    "emf",
    "art",
    "xar",
    "png",
    "webp",
    "jxr",
    "hdp",
    "wdp",
    "cur",
    "ecw",
    "iff",
    "lbm",
    "liff",
    "nrrd",
    "pam",
    "pcx",
    "pgf",
    "sgi",
    "rgb",
    "rgba",
    "bw",
    "int",
    "inta",
    "sid",
    "ras",
    "sun",
    "tga",
    "heic",
    "heif",
];
